import * as zod from 'zod'
import { COUNTRIES } from '@mote/helpers/constants'
import { QUOTE_FORM_ERROR_MESSAGE } from './constants'

function isNotEmptyString(value) {
  return typeof value === 'string' && value !== ''
}

function getMinQunatity(value) {
  const _value = isNotEmptyString(value) ? null : value
  const minValue = 10 - (_value || 0)

  return minValue > 0 ? minValue : 0
}

function getMessages({ minValue, value }) {
  const minMessage =
    minValue === 0 ? QUOTE_FORM_ERROR_MESSAGE.quantityNegativeError : ''

  const invalidMessage = isNotEmptyString(value)
    ? QUOTE_FORM_ERROR_MESSAGE.numberTypeError
    : minMessage

  return { minMessage, invalidMessage }
}

export const customValidationErrorMap = (issue, ctx) => {
  if (issue.code === zod.ZodIssueCode.invalid_enum_value) {
    const index = `${issue.path}Error`

    try {
      return { message: QUOTE_FORM_ERROR_MESSAGE[index] }
    } catch (e) {}
  }

  return { message: '' }
}

export function getQuoteFormSchema({
  isOther,
  studentLicenseQuantity,
  facultyLicenseQuantity
}) {
  const minFacultyQuantity = getMinQunatity(studentLicenseQuantity)
  const minStudentQuantity = getMinQunatity(facultyLicenseQuantity)
  const facultyMessages = getMessages({
    minValue: minFacultyQuantity,
    value: facultyLicenseQuantity
  })
  const studentMessages = getMessages({
    minValue: minStudentQuantity,
    value: studentLicenseQuantity
  })

  const schema = {
    facultyLicenseQuantity: zod
      .number({
        required_error: facultyMessages.minMessage,
        invalid_type_error: facultyMessages.invalidMessage
      })
      .min(minFacultyQuantity, { message: facultyMessages.minMessage }),
    studentLicenseQuantity: zod
      .number({
        required_error: studentMessages.minMessage,
        invalid_type_error: studentMessages.invalidMessage
      })
      .min(minStudentQuantity, { message: studentMessages.minMessage }),
    totalQuantity: zod
      .number({
        required_error: QUOTE_FORM_ERROR_MESSAGE.quantityMinError
      })
      .min(10, QUOTE_FORM_ERROR_MESSAGE.quantityMinError),
    PDHours: zod
      .number({
        required_error: QUOTE_FORM_ERROR_MESSAGE.quantityNegativeError,
        invalid_type_error: QUOTE_FORM_ERROR_MESSAGE.numberTypeError
      })
      .nonnegative(QUOTE_FORM_ERROR_MESSAGE.quantityNegativeError),
    termLength: zod.enum([1, 2, 3]),
    firstName: zod
      .string({ invalid_type_error: QUOTE_FORM_ERROR_MESSAGE.stringTypeError })
      .min(1, { message: QUOTE_FORM_ERROR_MESSAGE.firstNameError }),
    lastName: zod
      .string({ invalid_type_error: QUOTE_FORM_ERROR_MESSAGE.stringTypeError })
      .min(1, { message: QUOTE_FORM_ERROR_MESSAGE.lastNameError }),
    institutionName: zod
      .string({ invalid_type_error: QUOTE_FORM_ERROR_MESSAGE.stringTypeError })
      .min(1, { message: QUOTE_FORM_ERROR_MESSAGE.institutionError }),
    enquirerEmail: zod
      .string({ invalid_type_error: QUOTE_FORM_ERROR_MESSAGE.stringTypeError })
      .min(1, { message: QUOTE_FORM_ERROR_MESSAGE.emailError })
      .regex(/^.+@(?!(gmail|googlemail)).*\..+$/, {
        message: QUOTE_FORM_ERROR_MESSAGE.googleMailError
      })
      .email({ message: QUOTE_FORM_ERROR_MESSAGE.emailError }),
    phoneNumber: zod
      .string()
      .min(5, { message: QUOTE_FORM_ERROR_MESSAGE.phoneError }),
    countryCode: zod.enum(Object.keys(COUNTRIES)),
    region: zod
      .string({ invalid_type_error: QUOTE_FORM_ERROR_MESSAGE.stringTypeError })
      .min(1, { message: QUOTE_FORM_ERROR_MESSAGE.regionError }),
    quoteHandling: zod
      .string()
      .array()
      .nonempty({ message: QUOTE_FORM_ERROR_MESSAGE.quoteHandlingError }),
    additionalInfo: zod.string()
  }

  if (isOther)
    schema.otherUseDescription = zod
      .string()
      .min(1, { message: QUOTE_FORM_ERROR_MESSAGE.otherUseError })

  return zod.object(schema)
}
